import type { QueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'
import { toast } from 'sonner'
import { apiClient } from '@/shared/api/apiClient'
import { PRODUCTS_QUERY_KEY } from '@/shared/hooks/api/product'
import {
  MACHINERY_DIRECTIVE_E_PLAN_KEY,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { PRODUCT_SOURCES_KEY } from '@/shared/hooks/api/productSource'
import { normalizeFileName } from '@/shared/lib/normalize-file-name'
import { uploadFileToSignedOssUrl } from '@/shared/lib/uploadFileToSignedOssUrl'
import {
  assertUploadFileReadable,
  assertUploadFileSize,
  isPasswordProtectedFileError,
  isRightsProtectedFileError,
  isUploadTooLargeError,
} from '@/shared/lib/uploadGuardrails'

export type MachineryEPlanUploadTracking = {
  onSourceAdded?: (sourceId: string) => void
  onSourceFinished?: (sourceId: string) => void
}

async function invalidateUploadQueries(
  queryClient: QueryClient,
  productId: string,
  complianceId: string
): Promise<void> {
  await queryClient.invalidateQueries({ queryKey: [PRODUCT_SOURCES_KEY, productId] })
  await queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
  await queryClient.invalidateQueries({
    queryKey: [MACHINERY_DIRECTIVE_E_PLAN_KEY, complianceId],
  })
}

export async function kickoffMachineryEPlanUpload(args: {
  productId: string
  complianceId: string
  file: File
  queryClient: QueryClient
  translate: TFunction<'machineryDirectiveCompliance'>
  tracking?: MachineryEPlanUploadTracking
}): Promise<void> {
  const { productId, complianceId, file, queryClient, translate, tracking } =
    args

  const runUploadInBackground = async (sourceId: string) => {
    const normalizedFilename = normalizeFileName(file.name)
    const filePathInBucket = `sources/${sourceId}/${normalizedFilename}`
    try {
      const { data } = await apiClient.post<{ url: string }>(
        `/products/${productId}/sources/upload-url`,
        {
          key: file.name,
          sourceId,
          fileSizeBytes: file.size,
          contentType: 'application/x-www-form-urlencoded',
        }
      )
      await uploadFileToSignedOssUrl(data.url, file, {
        contentType: 'application/x-www-form-urlencoded',
        context: {
          feature: 'machinery-e-plan',
          fileName: file.name,
          fileSizeBytes: file.size,
          contentType: 'application/x-www-form-urlencoded',
          productId,
          sourceId,
          complianceId,
        },
      })
      const fileSizeKb = Math.ceil(file.size / 1024)
      await apiClient.patch(
        `/products/${productId}/sources/${sourceId}/file-path`,
        {
          filePathInBucket,
          fileSizeKb,
        }
      )
      await invalidateUploadQueries(queryClient, productId, complianceId)
      tracking?.onSourceFinished?.(sourceId)
      toast.success(translate('settings.ePlan.uploadSuccess'))
    } catch {
      try {
        await apiClient.patch(
          `/products/${productId}/sources/${sourceId}/upload-status`,
          { fileUploadStatus: 'FAILED' }
        )
      } catch {
        // Upload failed and status could not be persisted.
      }
      await invalidateUploadQueries(queryClient, productId, complianceId)
      tracking?.onSourceFinished?.(sourceId)
      toast.error(translate('settings.ePlan.uploadError'))
    }
  }

  try {
    assertUploadFileSize(file)
    await assertUploadFileReadable(file)
  } catch (error) {
    if (isUploadTooLargeError(error)) {
      toast.error(translate('settings.ePlan.fileTooLarge'))
      return
    }
    if (isPasswordProtectedFileError(error)) {
      toast.error(translate('settings.ePlan.fileEncrypted'))
      return
    }
    if (isRightsProtectedFileError(error)) {
      toast.error(translate('settings.ePlan.fileRightsProtected'))
      return
    }
    toast.error(translate('settings.ePlan.uploadError'))
    return
  }

  try {
    const sourceResponse = await apiClient.post<{ id: string }>(
      `/products/${productId}/sources`,
      {
        name: file.name,
        type: file.type || 'application/pdf',
      }
    )
    const sourceId = sourceResponse.data.id
    tracking?.onSourceAdded?.(sourceId)

    await apiClient.put(
      `/machinery-directive-compliance/${complianceId}/e-plan`,
      { productSourceId: sourceId }
    )

    await invalidateUploadQueries(queryClient, productId, complianceId)
    void runUploadInBackground(sourceId)
  } catch {
    toast.error(translate('settings.ePlan.uploadError'))
  }
}
